import { ref, computed, type Ref } from 'vue'
import { format, parseISO, subDays } from 'date-fns'
import Network from '@/Network'
import { getApiErrorMessage } from '@/utils/apiError'
import type { CheckAttemptItem } from '@/Network/Types/Responses/CheckAttemptsResponses'
import { ACCEPTED_COLOR, REJECTED_COLOR } from '@/components/CheckAttempts/checkAttemptsFormat'
import type { LatLng } from '@/utils/geo'
import type { DoctorPoint } from '../modals/UnitPinModal/pinModalTypes'

/** Janela do painel de tentativas: 14 dias cobrem duas semanas de escala. */
const WINDOW_DAYS = 14
/** Teto do desenho no mapa — acima disso o mapa vira mancha e a chamada pesa. */
const PAGE_LIMIT = 200

const DEFAULT_ERROR = 'Não foi possível carregar as tentativas de check'

function toPoint(attempt: CheckAttemptItem): DoctorPoint | null {
  const { checkLatitude: lat, checkLongitude: lng } = attempt
  // Tentativa sem GPS (permissão negada, localização indisponível) não tem onde ser desenhada.
  if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return null

  const accepted = attempt.outcome === 'ACCEPTED'
  const doctorName = attempt.doctor?.name ?? 'Médico não identificado'
  const when = format(parseISO(attempt.createdAt), 'dd/MM HH:mm')

  return {
    id: attempt.id,
    lat,
    lng,
    color: accepted ? ACCEPTED_COLOR : REJECTED_COLOR,
    label: `${doctorName} · ${when} · ${accepted ? 'Aceito' : 'Rejeitado'}`,
    accepted
  }
}

/**
 * Onde os médicos realmente bateram ponto nos últimos 14 dias — a prova visual
 * de que o pino está (ou não) sobre o prédio certo. Sem unidade (cadastro novo)
 * não há histórico a buscar.
 */
export function useUnitDoctorPoints(healthUnitId: Ref<number | null>) {
  const points = ref<DoctorPoint[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  /** Contagens derivadas dos mesmos pontos desenhados no mapa (não do total da API). */
  const distinctDoctors = ref(0)
  const distinctDays = ref(0)

  const summary = computed(() => {
    if (!points.value.length) return null
    return {
      total: points.value.length,
      rejected: points.value.filter((point) => !point.accepted).length,
      doctors: distinctDoctors.value,
      days: distinctDays.value
    }
  })

  const centroid = computed<LatLng | null>(() => {
    if (!points.value.length) return null
    const sum = points.value.reduce(
      (acc, point) => ({ lat: acc.lat + point.lat, lng: acc.lng + point.lng }),
      { lat: 0, lng: 0 }
    )
    return { lat: sum.lat / points.value.length, lng: sum.lng / points.value.length }
  })

  async function load(): Promise<void> {
    if (!healthUnitId.value) return

    isLoading.value = true
    error.value = null
    try {
      const today = new Date()
      const response = await Network.checkAttempts.listByHealthUnit(healthUnitId.value, {
        startDate: format(subDays(today, WINDOW_DAYS), 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd'),
        page: 1,
        limit: PAGE_LIMIT
      })

      const attempts = response.data ?? []
      const mapped = attempts
        .map((attempt) => ({ attempt, point: toPoint(attempt) }))
        .filter(
          (entry): entry is { attempt: CheckAttemptItem; point: DoctorPoint } => !!entry.point
        )

      points.value = mapped.map((entry) => entry.point)
      distinctDoctors.value = new Set(
        mapped.map((entry) => entry.attempt.doctor?.id).filter((id) => id != null)
      ).size
      // Mesmo fuso do label do ponto: cortar o ISO em UTC jogaria um check das 21h
      // de Brasília para o dia seguinte e inflaria a contagem de dias.
      distinctDays.value = new Set(
        mapped.map((entry) => format(parseISO(entry.attempt.createdAt), 'yyyy-MM-dd'))
      ).size
    } catch (e) {
      // Funil canônico: ler o `.message` cru vazava o texto técnico de um TypeError.
      error.value = getApiErrorMessage(e, DEFAULT_ERROR)
      points.value = []
      distinctDoctors.value = 0
      distinctDays.value = 0
    } finally {
      isLoading.value = false
    }
  }

  return { points, isLoading, error, summary, centroid, load }
}

export type UnitDoctorPointsApi = ReturnType<typeof useUnitDoctorPoints>
